import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

import { AuthContext } from '../components/AuthProvider';
import { get_users } from "../tools-network";
import { showTemporaryMessage } from "../components/Message";

/**
 * filter is an optional argument that filters users to be returned
 */

const PickUser = ({loginToken, setSelectedUserId, refreshUserListTrigger, filter}) => {
	const { t } = useTranslation();
	const { handleInvalidToken } = React.useContext(AuthContext);
	const [users, setUsers] = useState([]);
	const [selectedUserData, setSelectedUserData] = useState(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const updateListOfUsers = async () => {
		try {
			const res = await get_users(loginToken, '');
			if(res.status) {
				let updateUsers = res.data;
				if(filter) {
					updateUsers = updateUsers.filter(filter);
				}
				setUsers(updateUsers);
				setSelectedUserData(null);
				setSelectedUserId(0);
			} else {
				showTemporaryMessage("Fetch users FAILED!", res.msg, 'danger');
				handleInvalidToken();
			}
		} catch (err) {
			showTemporaryMessage("Fetch users FAILED!", err, 'danger');
			handleInvalidToken();
		}
	};

	useEffect(() => {
		updateListOfUsers();
	}, [refreshUserListTrigger]);

    const handleUserChange = (user) => {
        setSelectedUserData(user);
		setSelectedUserId(user.id);
		setIsDropdownOpen(false);
		setSearchQuery('');
    };

	const filteredUsers = users.filter(user => 
		user.name.toLowerCase().includes(searchQuery.toLowerCase())
	);

	const handleSearchChange = (e) => {
		setSearchQuery(e.target.value);
		if (e.target.value.length > 0) {
			setIsDropdownOpen(true);
		}
	};

	return (
		<>
			<div className="w-full">
				<h2 className="font-bold text-xl">{t("select_user")}</h2>
				<div className="flex flex-row gap-4">
					
					{/* User details */}
					<div className="flex-1">
						<div className="basis-1/3">
							{/* Search input */}
							<div className="mt-2 mb-2">
								<input
									type="text"
									placeholder={t("search_users")}
									value={searchQuery}
									onChange={handleSearchChange}
									onFocus={() => searchQuery.length > 0 && setIsDropdownOpen(true)}
									className="w-full rounded-md border-gray-300 shadow-sm px-3 py-2 text-sm focus:border-cyan-500 focus:ring-cyan-500"
								/>
							</div>
							<Listbox
								as="div"
								value={selectedUserData}
								onChange={handleUserChange}
								open={isDropdownOpen}
								onClose={() => setIsDropdownOpen(false)}>
								{({ open }) => (
									<>
										<div className="relative mt-2">
											<Listbox.Button 
												className="inline-flex w-full justify-between gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none"
												onClick={() => setIsDropdownOpen(!isDropdownOpen)}
											>
												<span>{selectedUserData ? selectedUserData.name : t("select_user")}</span>
												<ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
											</Listbox.Button>
											<Transition
												show={isDropdownOpen}
												as={Fragment}
												enter="transition ease-out duration-100"
												enterFrom="transform opacity-0 scale-95"
												enterTo="transform opacity-100 scale-100"
												leave="transition ease-in duration-75"
												leaveFrom="transform opacity-100 scale-100"
												leaveTo="transform opacity-0 scale-95">

												<Listbox.Options
													className="absolute right-0 z-10 mt-1 w-full max-h-[80vh] overflow-auto origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
													static
													>
													{filteredUsers.length > 0 ? (
														filteredUsers.map((user, index) => (
															<Listbox.Option key={index} value={user}>
																{({ active }) => (
																	<div
																		className={`${
																			active ? 'bg-gray-100' : ''
																		} cursor-pointer select-none relative py-2 pl-3 pr-9`}
																		>
																		<span>{user.name}</span>
																	</div>
																)}
															</Listbox.Option>
														))
													) : (
														<div className="py-2 pl-3 pr-9 text-gray-500 text-sm">
															{t("no_users_found")}
														</div>
													)}
												</Listbox.Options>
											</Transition>
										</div>
									</>
								)}
							</Listbox>
						</div>
					</div>
				</div>
			</div>
		</>
	)
};

export default PickUser;
